// v4 (V4-U1, spec-v4-extras.md row A3 "Undo any single change"): the web's client for a video's change history and
// undo (api/src/v4/routes-changes.ts), and the pure helpers the History fold and the Undo toast use. Undo is free: it
// puts back what one change replaced; whatever was made stays in the library. Under VITE_MOCK the history lives in
// memory here, so the fold works in the mock app too.
import { ApiError, call } from "./api";
import { bump, toast } from "./store";

export type ChangeKind = "change" | "undo" | "redo";
export type ChangeItem = {
  id: string;
  projectId: string | null;
  op: string;
  label: string;
  at: string;
  credits: number;
  kind: ChangeKind;
  undoes: string | null;
  /** an original change that is undone right now */
  undone: boolean;
  /** whether Undo (or Redo, when undone) works right now */
  canUndo: boolean;
  why: string | null;
};
export type UndoAnswer = { change: ChangeItem; undo: ChangeItem; redo: boolean; skipped: number };

const obj = (v: unknown): Record<string, unknown> => (v && typeof v === "object" ? (v as Record<string, unknown>) : {});
const s = (v: unknown): string => (typeof v === "string" ? v : "");

/** One change from the API (or an SSE event), checked. Null when it isn't one. */
export function normChange(raw: unknown): ChangeItem | null {
  const o = obj(raw);
  if (!s(o.id)) return null;
  const kind = o.kind === "undo" || o.kind === "redo" ? o.kind : "change";
  return {
    id: s(o.id),
    projectId: s(o.projectId) || null,
    op: s(o.op),
    label: s(o.label) || "A change",
    at: s(o.at),
    credits: typeof o.credits === "number" ? o.credits : 0,
    kind,
    undoes: s(o.undoes) || null,
    undone: o.undone === true,
    canUndo: o.canUndo !== false,
    why: s(o.why) || null,
  };
}

export const normChanges = (raw: unknown): ChangeItem[] => (Array.isArray(raw) ? raw : []).map(normChange).filter((x): x is ChangeItem => !!x);

/** The button on a history row: "Undo", "Redo" (when undone), and whether it can be tapped now. */
export function rowAction(c: ChangeItem): { label: "Undo" | "Redo"; enabled: boolean; hint: string | null } {
  return { label: c.undone ? "Redo" : "Undo", enabled: c.canUndo, hint: c.canUndo ? null : c.why };
}

/** The toast for a new change: what it did, with Undo; for an undo, with Redo. */
export function toastFor(c: ChangeItem): { text: string; action: "Undo" | "Redo" } {
  return { text: c.label, action: c.kind === "change" ? "Undo" : "Redo" };
}

/** The history fold's one-line summary ("Deleted scene 3 · 2 more"). */
export function historySummary(cs: ChangeItem[]): string {
  const live = cs.filter((c) => !c.undone);
  if (!cs.length) return "Nothing changed yet";
  const first = (live[0] ?? cs[0])!;
  return cs.length > 1 ? `${first.label} · ${cs.length - 1} more` : first.label;
}

// ------------------------------------------------------------------ the SSE event: a toast with Undo

const shown = new Set<string>();

/**
 * X3: a change made by one of the Director's tools during a call is said aloud ("Done. Say undo to put it back"), so
 * it gets no toast on top: the History fold still has it. Counts the tool calls in flight, plus a short grace for
 * the SSE event that lands just after the tool's answer.
 */
let toolCalls = 0;
let quietUntil = 0;
export const TOOL_TOAST_GRACE_MS = 2500;
export async function fromDirectorTool<T>(p: Promise<T>): Promise<T> {
  toolCalls++;
  try {
    return await p;
  } finally {
    toolCalls--;
    quietUntil = Date.now() + TOOL_TOAST_GRACE_MS;
  }
}
/** Whether a change arriving now came from a Director tool (no toast for it). */
export const directorToolBusy = (now = Date.now()) => toolCalls > 0 || now < quietUntil;

/** A change arrived (from the Director, a tap, a note's fix): refresh the history and offer Undo (or Redo) for it. */
export function onChangeEvent(raw: unknown) {
  const c = normChange(raw);
  if (!c) return;
  bump("changes");
  if (shown.has(c.id)) return;
  shown.add(c.id);
  if (directorToolBusy()) return;
  const t = toastFor(c);
  toast(t.text, "info", { label: t.action, run: () => void runUndo(c) });
}

// ------------------------------------------------------------------ source (http, or memory under VITE_MOCK)

const isMock = () => import.meta.env?.VITE_MOCK === "1";
let mock: ChangeItem[] | null = null;
const mockList = () =>
  (mock ??= [
    { id: "00000000-0000-4000-8000-0000000000c3", projectId: null, op: "delete_scene", label: "Deleted scene 4", at: new Date(Date.now() - 120_000).toISOString(), credits: 0, kind: "change", undoes: null, undone: false, canUndo: true, why: null },
    { id: "00000000-0000-4000-8000-0000000000c2", projectId: null, op: "redo_frame", label: "Redid scene 2's frame", at: new Date(Date.now() - 900_000).toISOString(), credits: 15, kind: "change", undoes: null, undone: false, canUndo: true, why: null },
    { id: "00000000-0000-4000-8000-0000000000c1", projectId: null, op: "update_brief", label: "Changed the brief", at: new Date(Date.now() - 3_600_000).toISOString(), credits: 0, kind: "change", undoes: null, undone: true, canUndo: true, why: null },
  ]);

export async function loadChanges(projectId: string): Promise<ChangeItem[]> {
  if (isMock()) return structuredClone(mockList()).map((c) => ({ ...c, projectId }));
  return normChanges(obj(await call("GET", `projects/${encodeURIComponent(projectId)}/changes`)).changes);
}

export async function undoChange(id: string): Promise<UndoAnswer> {
  if (isMock()) {
    const c = mockList().find((x) => x.id === id || `${x.id}-u` === id);
    if (!c) throw new ApiError("Change not found.", 404);
    c.undone = !c.undone;
    const undo: ChangeItem = { ...c, id: `${c.id}-u`, kind: c.undone ? "undo" : "redo", label: `${c.undone ? "Undid" : "Redid"}: ${c.label}`, undoes: c.id, undone: false };
    return { change: { ...c }, undo, redo: !c.undone, skipped: 0 };
  }
  const r = obj(await call("POST", `changes/${encodeURIComponent(id)}/undo`, {}));
  return { change: normChange(r.change)!, undo: normChange(r.undo)!, redo: r.redo === true, skipped: typeof r.skipped === "number" ? r.skipped : 0 };
}

/**
 * Taps Undo (or Redo): runs it, refreshes what it changed and says what happened. A refusal (changed again since,
 * still being made) is a toast with the API's own words. Returns whether it worked.
 */
export async function runUndo(c: Pick<ChangeItem, "id">): Promise<boolean> {
  try {
    // an undo row's id means its change (the API redoes it)
    const r = await undoChange(c.id);
    shown.add(r.undo.id); // the SSE event for it won't toast again
    bump("project", "projects", "changes", "library", "channels");
    const text = r.redo ? `Back: ${r.change.label}` : `Undone: ${r.change.label}${r.skipped ? " (partly: some of it was removed since)" : ""}`;
    toast(text, "good", { label: r.redo ? "Undo" : "Redo", run: () => void runUndo(r.change) });
    return true;
  } catch (e) {
    toast(e instanceof Error ? e.message : "Couldn't undo that.", "bad");
    return false;
  }
}
